import { Avatar, Button, notification, Select, Spin } from 'antd';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Group from '../../interfaces/Group';
import { GetGroupsPayload } from '../../interfaces/GroupService';
import User from '../../interfaces/User';
import { GetUsersPayload } from '../../interfaces/UserService';
import { GroupService } from '../../services/groupService';
import { UserService } from '../../services/userService';
import { validateEmail } from '../../utils/utils';
import { Icon } from '../Core/Icon';
import TruncatedTooltip from './TruncatedTooltip';

export interface Props {
  label?: string;
  mode?: 'multiple' | 'tags';
  description?: string | null;
  errorMessage?: string;
  className?: string;
  includeUsers?: boolean;
  includeGroups?: boolean;
  disabled?: boolean;
  placeholder?: string;
  selectedUserIds?: number[];
  selectedGroupIds?: number[];
  onUserChange?: (ids: number[], values: User[]) => void;
  onGroupChange?: (ids: number[], values: Group[]) => void;
  onReset?: () => void;
  additionalUserFilters?: Partial<GetUsersPayload>;
  additionalGroupFilters?: Partial<GetGroupsPayload>;
  allowClear?: boolean;
  maxTagCount?: number;
  closeOnSelect?: boolean;
  autoClearSearchValue?: boolean;
  size?: 'large' | 'middle' | 'small';
  selectClassName?: string;
  ignoredUserIds?: number[];
  ignoredGroupIds?: number[];
  forbiddenTagNames?: string[];
  variant?: 'outlined' | 'borderless' | 'filled' | 'underlined';
}

const userService = new UserService();
const groupService = new GroupService();

interface DataMap {
  [key: string]: User | Group;
}

export default function UserMultiSelect(props: Props) {
  const {
    label,
    description,
    errorMessage,
    className,
    placeholder = 'Search for users or groups',
    includeUsers = true,
    includeGroups = true,
    disabled,
    selectedUserIds = [],
    selectedGroupIds = [],
    onUserChange,
    onGroupChange,
    onReset,
    additionalUserFilters,
    additionalGroupFilters,
    allowClear = true,
    maxTagCount = 0,
    closeOnSelect = false,
    autoClearSearchValue = false,
    size = 'middle',
    selectClassName = 'w-full',
    mode = 'multiple',
    ignoredUserIds = [],
    ignoredGroupIds = [],
    forbiddenTagNames = [],
    variant = 'outlined',
  } = props;

  const [notificationApi, notificationContextHolder] =
    notification.useNotification();
  const { t } = useTranslation();

  // Data and pagination states
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Selected values and data lookup map
  const [values, setValues] = useState<string[]>([]);
  const [dataMap, setDataMap] = useState<DataMap>({});

  // Filters for server-side search
  const [userFilters, setUserFilters] = useState<GetUsersPayload>({
    count: 15,
    offset: 0,
    search: '',
    ...(additionalUserFilters || {}),
  });
  const [groupFilters, setGroupFilters] = useState<GetGroupsPayload>({
    count: 15,
    offset: 0,
    search: '',
    ...(additionalGroupFilters || {}),
  });
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  /**
   * Fetch users with disabled users filtered out
   */
  const fetchUsers = useCallback(
    async (filters: GetUsersPayload, reset = false) => {
      setIsLoadingUsers(true);
      try {
        const res = await userService.getUsers(filters);
        const raw = res.users || [];
        // Filter out ignored users
        const filtered = raw.filter((u) => !ignoredUserIds.includes(u.userId));
        setUsers((prev) => (reset ? filtered : [...prev, ...filtered]));
        const removed = raw.length - filtered.length;
        setTotalUsers((prevTotal) =>
          reset ? (res.total ?? raw.length) - removed : prevTotal - removed
        );
      } catch {
        notificationApi.error({
          message: t('errors.genericFetchError', {
            resource: t('common.users'),
          }),
        });
      } finally {
        setIsLoadingUsers(false);
      }
    },
    []
  );

  /**
   * Fetch groups with disabled groups filtered out
   */
  const fetchGroups = useCallback(
    async (filters: GetGroupsPayload, reset = false) => {
      setIsLoadingGroups(true);
      try {
        const res = await groupService.getGroups(filters);
        const raw = res.groups || [];
        const filtered = raw.filter((g) => !ignoredGroupIds.includes(g.id));
        setGroups((prev) => (reset ? filtered : [...prev, ...filtered]));
        const removed = raw.length - filtered.length;
        setTotalGroups((prevTotal) =>
          reset ? (res.total ?? raw.length) - removed : prevTotal - removed
        );
      } catch {
        notificationApi.error({
          message: t('errors.genericFetchError', {
            resource: t('common.groups'),
          }),
        });
      } finally {
        setIsLoadingGroups(false);
      }
    },
    []
  );

  // Infinite scroll load more
  const handleLoadMore = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLDivElement;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 10) return;

    if (includeUsers && users.length < totalUsers && !isLoadingUsers) {
      const uf = {
        ...userFilters,
        offset: userFilters.offset + userFilters.count,
      };
      setUserFilters(uf);
      void fetchUsers(uf, false);
    }
    if (includeGroups && groups.length < totalGroups && !isLoadingGroups) {
      const gf = {
        ...groupFilters,
        offset: groupFilters.offset + groupFilters.count,
      };
      setGroupFilters(gf);
      void fetchGroups(gf, false);
    }
  };

  // Debounced search
  const handleSearch = (keyword: string) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      const uf = { ...userFilters, search: keyword, offset: 0 };
      const gf = { ...groupFilters, search: keyword, offset: 0 };
      setUserFilters(uf);
      setGroupFilters(gf);
      if (includeUsers) void fetchUsers(uf, true);
      if (includeGroups) void fetchGroups(gf, true);
    }, 300);
    setDebounceTimer(timer);
  };

  // Build raw options from fetched data
  const rawOptions = useMemo(() => {
    const groupOpts = groups.map((g) => ({
      value: `group-${g.id}`,
      //disabled: disabledGroupIds.includes(g.id),
      data: g,
      label: (
        <div className="flex items-center gap-2">
          <Avatar size={32} className="bg-azure-hover">
            <Icon name="UsersIcon" className="h-4 w-4 text-white" />
          </Avatar>
          <div className="flex flex-col">
            <span className="text-blue-dark">{g.name}</span>
            <span className="text-sm text-gray-dark">
              {g.usersCount} members
            </span>
          </div>
        </div>
      ),
    }));

    const userOpts = users.map((u) => ({
      value: `user-${u.userId}`,
      //disabled: disabledUserIds.includes(u.userId),
      data: u,
      label: (
        <div className="flex items-center gap-2 truncate">
          <Avatar size={32} className="min-w-[32px] bg-azure-hover">
            {u.firstName?.[0]?.toUpperCase() ||
              u.lastName?.[0]?.toUpperCase() ||
              u.email?.[0]?.toUpperCase()}
          </Avatar>
          <div className="flex flex-col truncate text-sm">
            <span className="truncate text-blue-dark">
              <TruncatedTooltip>
                {u.firstName} {u.lastName}
              </TruncatedTooltip>
            </span>
            <span className="truncate text-sm text-gray-dark">
              <TruncatedTooltip>{u.email}</TruncatedTooltip>
            </span>
          </div>
        </div>
      ),
    }));

    return [...groupOpts, ...userOpts];
  }, [groups, users]);

  // Ensure selected values remain in options after search
  const mergedOptions = useMemo(() => {
    const map = new Map<string, any>(rawOptions.map((o) => [o.value, o]));
    values.forEach((v) => {
      if (!map.has(v) && dataMap[v]) {
        const item = dataMap[v];

        map.set(v, {
          value: v,
          data: item,

          label: v.startsWith('user-')
            ? (item as User)?.firstName || (item as User)?.lastName
              ? `${(item as User)?.firstName} ${(item as User)?.lastName}`
              : (item as User)?.email
            : (item as Group).name,
        });
      }
    });

    return Array.from(map.values());
  }, [rawOptions, values, dataMap]);

  // Unified change handler
  const handleChange = (newVals: string[], selectedOptions: any | any[]) => {
    const externalTags = newVals.filter(
      (v) => !v.startsWith('user-') && !v.startsWith('group-')
    );

    const forbiddenExternalTags = externalTags.filter((v) =>
      forbiddenTagNames.includes(v)
    );

    if (forbiddenExternalTags.length) {
      notificationApi.warning({
        message: t('errors.someItemsCannotBeSelected'),
      });
      return false;
    }

    // For safety, check if the selected values are allowed
    const forbidden = newVals.filter((v) => {
      if (v.startsWith('user-')) {
        const id = parseInt(v.replace('user-', ''), 10);
        return ignoredUserIds.includes(id);
      }
      if (v.startsWith('group-')) {
        const id = parseInt(v.replace('group-', ''), 10);
        return ignoredGroupIds.includes(id);
      }
      return false;
    });

    let filtered = newVals;
    if (forbidden.length) {
      notificationApi.warning({
        message: t('errors.someItemsCannotBeSelected'),
      });
      filtered = newVals.filter((v) => !forbidden.includes(v));
    }

    // Build updated map including newly selected
    const opts = Array.isArray(selectedOptions)
      ? selectedOptions
      : [selectedOptions];
    const newMap: DataMap = { ...dataMap };
    opts.forEach((o) => {
      if (o.value) {
        newMap[o.value] = o.data;
      }
    });

    // Extract IDs
    const uids = filtered
      .filter((v) => v.startsWith('user-'))
      .map((v) => parseInt(v.replace('user-', ''), 10));
    const gids = filtered
      .filter((v) => v.startsWith('group-'))
      .map((v) => parseInt(v.split('-')[1], 10));

    const externalUsers = filtered.filter(
      (v) => !(v.startsWith('user-') || v.startsWith('group-'))
    );

    if (mode === 'tags' && externalUsers.length > 0) {
      externalUsers.forEach((email, index) => {
        const findExt = Object.values(newMap).find(
          (u) => u && 'email' in u && u.email === email
        ) as User;

        if (!findExt && validateEmail(email)) {
          const extId = -(index + (selectedUserIds.length + 1));
          newMap[`user-${extId}`] = {
            userId: extId,
            email,
          };
          uids.push(extId);
          newVals.push(`user-${extId}`);
        }
      });
    }

    // Update state
    setValues(
      newVals.filter((v) => v.startsWith('user-') || v.startsWith('group-'))
    );
    setDataMap(newMap);

    if (uids.length === 0 && gids.length == 0 && onReset) {
      onReset();
    } else {
      // Notify parents with correct data from newMap
      if (
        includeUsers &&
        onUserChange &&
        selectedUserIds.length != uids.length // added to dispatch event only if something has changed
      ) {
        onUserChange(
          uids,
          uids.map((id) => newMap[`user-${id}`] as User)
        );
      }
      if (
        includeGroups &&
        onGroupChange &&
        selectedGroupIds.length != gids.length // added to dispatch event only if something has changed
      ) {
        onGroupChange(
          gids,
          gids.map((id) => newMap[`group-${id}`] as Group)
        );
      }
    }

    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  // Sync props selection
  useEffect(() => {
    const merged: string[] = [];

    if (selectedUserIds) {
      merged.push(...selectedUserIds.map((id) => `user-${id}`));
    }

    if (selectedGroupIds) {
      merged.push(...selectedGroupIds.map((id) => `group-${id}`));
    }

    // Compare arrays before updating to prevent infinite loops

    const isEqual =
      merged.length === values.length &&
      merged.every((value, index) => value === values[index]);

    if (!isEqual) {
      setValues(merged);
    }
  }, [selectedUserIds, selectedGroupIds]);

  // Initial data load
  useEffect(() => {
    if (includeUsers) void fetchUsers(userFilters, true);
    if (includeGroups) void fetchGroups(groupFilters, true);
  }, []);

  const loading = isLoadingUsers || isLoadingGroups;
  const finalPlaceholder =
    values.length > 0
      ? `${values.length} ${t('common.selected')}`
      : placeholder;

  return (
    <div className={className}>
      {notificationContextHolder}
      {label && <label>{label}</label>}
      {description && <p className="text-sm text-gray-dark">{description}</p>}
      <Select<string[]>
        className={selectClassName}
        mode={mode}
        variant={variant}
        size={size}
        open={isOpen}
        value={values}
        disabled={disabled}
        allowClear={allowClear}
        autoClearSearchValue={autoClearSearchValue}
        maxTagCount={maxTagCount}
        maxTagPlaceholder={(omitted) => `+${omitted.length} selected`}
        placeholder={finalPlaceholder}
        loading={loading}
        showSearch
        filterOption={false}
        options={mergedOptions}
        onOpenChange={setIsOpen}
        onSearch={handleSearch}
        onPopupScroll={handleLoadMore}
        onChange={handleChange}
        tagRender={(props) => {
          if (props.isMaxTag) {
            return (
              <div className="ant-select-selection-overflow-item ant-select-selection-overflow-item-rest">
                <span
                  title={`${props.label}`}
                  className="ant-select-selection-item"
                >
                  <span className="ant-select-selection-item-content">
                    {props.label}
                  </span>
                </span>
              </div>
            );
          }

          const { value, onClose } = props;
          const item = dataMap?.[value];
          const isUser = value?.startsWith('user-');

          return (
            <div className="m-0.5 flex items-center gap-1 rounded border p-1">
              <Avatar
                size={24}
                className="flex-shrink-0 bg-azure-hover text-center text-sm uppercase"
              >
                {isUser ? (
                  <span className="flex items-center justify-center text-sm uppercase">
                    {(item as User)?.firstName?.[0]?.toUpperCase()}
                  </span>
                ) : (
                  <Icon name="UsersIcon" />
                )}
              </Avatar>
              <span className="flex items-center justify-center text-sm text-blue-dark">
                {isUser
                  ? (item as User)?.firstName || (item as User)?.lastName
                    ? `${(item as User)?.firstName} ${(item as User)?.lastName}`
                    : (item as User)?.email
                  : (item as Group)?.name}
              </span>
              <Button
                disabled={disabled}
                type="text"
                size="small"
                onClick={onClose}
              >
                <Icon name="XIcon" className="h-4 w-4 text-gray-dark" />
              </Button>
            </div>
          );
        }}
        notFoundContent={loading ? <Spin size="small" /> : null}
      />
      {errorMessage && <p className="text-red-500 mt-1">{errorMessage}</p>}
    </div>
  );
}
